import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiUpload, FiX, FiImage, FiFileText } from "react-icons/fi";
import { COLORS } from "../utils/constants";
import { fileToBase64, isReceiptFile } from "../utils/helpers";

const DropZone = styled.div`
  align-items: center;
  background: ${(props) =>
    props.$isDragging ? COLORS.primaryLight : COLORS.gray50};
  border: 2px dashed
    ${(props) => (props.$isDragging ? COLORS.primary : COLORS.gray300)};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  min-height: 120px;
  padding: 20px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${COLORS.primary};
    background: ${COLORS.primaryLight};
  }
`;

const DropText = styled.p`
  color: ${COLORS.gray500};
  font-size: 14px;
  margin: 0;
  text-align: center;
`;

const DropHint = styled.span`
  color: ${COLORS.gray400};
  font-size: 12px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const UploadIcon = styled(FiUpload)`
  color: ${COLORS.gray400};
`;

const PreviewGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const PreviewItem = styled.div`
  position: relative;
  width: 140px;
`;

const PreviewThumb = styled.img`
  border: 1px solid ${COLORS.gray200};
  border-radius: 8px;
  height: 100px;
  object-fit: cover;
  width: 140px;
`;

const PdfThumb = styled.div`
  align-items: center;
  background: ${COLORS.gray50};
  border: 1px solid ${COLORS.gray200};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100px;
  justify-content: center;
  width: 140px;
`;

const PdfThumbLabel = styled.span`
  color: ${COLORS.gray600};
  font-size: 11px;
`;

const ItemRemoveButton = styled.button`
  align-items: center;
  background: ${COLORS.danger};
  border: none;
  border-radius: 50%;
  color: ${COLORS.white};
  cursor: pointer;
  display: flex;
  height: 22px;
  justify-content: center;
  position: absolute;
  right: 4px;
  top: 4px;
  width: 22px;

  &:hover {
    background: #b91c1c;
  }
`;

const ReceiptUpload = (props) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const receipts = props.receipts || [];

  const handleFile = useCallback(
    async (file) => {
      if (!isReceiptFile(file)) {
        alert("Please upload an image (JPEG, PNG, GIF) or PDF file.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert("File is too large. Please upload a file under 5MB.");
        return;
      }
      try {
        const base64 = await fileToBase64(file);
        props.onReceiptsChange([...props.receipts, base64]);
      } catch (error) {
        console.error("Error processing file:", error);
      }
    },
    [props],
  );

  const handleFiles = useCallback(
    (files) => {
      Array.from(files).forEach((file) => handleFile(file));
    },
    [handleFile],
  );

  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (
          items[i].type.startsWith("image/") ||
          items[i].type === "application/pdf"
        ) {
          e.preventDefault();
          const file = items[i].getAsFile();
          handleFile(file);
          break;
        }
      }
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [handleFile]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e) => {
    if (e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    e.target.value = "";
  };

  const handleRemove = (index) => {
    props.onReceiptsChange(receipts.filter((_, i) => i !== index));
  };

  return (
    <React.Fragment>
      {receipts.length > 0 && (
        <PreviewGrid>
          {receipts.map((r, i) => {
            const isPdf = r.startsWith("data:application/pdf");
            return (
              <PreviewItem key={i}>
                {isPdf ? (
                  <PdfThumb>
                    <FiFileText size={24} color={COLORS.gray500} />
                    <PdfThumbLabel>PDF</PdfThumbLabel>
                  </PdfThumb>
                ) : (
                  <PreviewThumb src={r} alt={`Receipt ${i + 1}`} />
                )}
                <ItemRemoveButton
                  type="button"
                  onClick={() => handleRemove(i)}
                  title="Remove"
                >
                  <FiX size={12} />
                </ItemRemoveButton>
              </PreviewItem>
            );
          })}
        </PreviewGrid>
      )}
      <DropZone
        ref={dropZoneRef}
        $isDragging={isDragging}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <UploadIcon size={24} />
        <DropText>
          <FiImage style={{ marginRight: 4, verticalAlign: "middle" }} />
          {receipts.length > 0
            ? "Add more receipts"
            : "Drop receipts here or click to upload"}
        </DropText>
        <DropHint>Supports JPEG, PNG, GIF, PDF -- max 5MB each</DropHint>
        <DropHint>You can also paste (Ctrl+V / Cmd+V) from clipboard</DropHint>
      </DropZone>
      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        onChange={handleInputChange}
      />
    </React.Fragment>
  );
};

export default ReceiptUpload;
